import { computed, type Ref } from "vue";
import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import type { ApiError } from "@/helpers/api-error";
import type {
  Schedule,
  ScheduleRun,
  CreateScheduleRequest,
  UpdateScheduleRequest,
  PreviewNextRunsRequest,
} from "@/types/schedule-types";

const BASE = `${import.meta.env.VITE_API_URL}/schedules`;

/**
 * Server-side cap on retained run-log entries (mirrors Schedule.RunLogCap in the domain). The API
 * never returns more than this, so a full list means older entries have been trimmed.
 */
export const RUN_LOG_CAP = 50;

// READ — all schedules for the current org, optionally filtered by project. Pass `enabled` to defer
// the fetch (e.g. only when a tab is active).
export function useSchedules(projectId?: Ref<string | null>, enabled?: Ref<boolean>) {
  const { data, isFetching: schedulesLoading, error: schedulesError } = useQuery({
    queryKey: computed(() => ["schedules", projectId?.value ?? null]),
    queryFn: () => {
      const qs = projectId?.value ? `?projectId=${encodeURIComponent(projectId.value)}` : "";
      return fetchWrapper.get<Schedule[]>(`${BASE}/${qs}`);
    },
    enabled: computed(() => (enabled ? enabled.value : true)),
  });
  const schedules = computed(() => data.value ?? []);
  return { schedules, schedulesLoading, schedulesError: schedulesError as Ref<ApiError | null> };
}

// READ — single schedule.
export function useSchedule(id: Ref<string>) {
  const { data: schedule, isFetching: scheduleLoading, error: scheduleError } = useQuery({
    queryKey: computed(() => ["schedule", id.value]),
    queryFn: () => fetchWrapper.get<Schedule>(`${BASE}/${id.value}`),
    enabled: computed(() => !!id.value),
  });
  return { schedule, scheduleLoading, scheduleError: scheduleError as Ref<ApiError | null> };
}

// READ — firing history for a schedule.
export function useScheduleRuns(id: Ref<string>) {
  const { data, isFetching: scheduleRunsLoading, error: scheduleRunsError } = useQuery({
    queryKey: computed(() => ["schedule-runs", id.value]),
    queryFn: () => fetchWrapper.get<ScheduleRun[]>(`${BASE}/${id.value}/runs`),
    enabled: computed(() => !!id.value),
  });
  const scheduleRuns = computed(() => data.value ?? []);
  return { scheduleRuns, scheduleRunsLoading, scheduleRunsError: scheduleRunsError as Ref<ApiError | null> };
}

// WRITE — create.
export function useCreateSchedule() {
  const queryClient = useQueryClient();
  const { mutate, mutateAsync, isPending, error, reset } = useMutation<string, ApiError, CreateScheduleRequest>({
    mutationFn: (payload) => fetchWrapper.post<string>(`${BASE}/`, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["schedules"] }),
  });
  return {
    createSchedule: mutate,
    createScheduleAsync: mutateAsync,
    createSchedulePending: isPending,
    createScheduleError: error as Ref<ApiError | null>,
    createScheduleReset: reset,
  };
}

// WRITE — update.
export function useUpdateSchedule() {
  const queryClient = useQueryClient();
  const { mutate, mutateAsync, isPending, error, reset } = useMutation<
    unknown,
    ApiError,
    { id: string; payload: UpdateScheduleRequest }
  >({
    mutationFn: ({ id, payload }) => fetchWrapper.put(`${BASE}/${id}`, payload),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["schedules"] });
      queryClient.invalidateQueries({ queryKey: ["schedule", vars.id] });
    },
  });
  return {
    updateSchedule: mutate,
    updateScheduleAsync: mutateAsync,
    updateSchedulePending: isPending,
    updateScheduleError: error as Ref<ApiError | null>,
    updateScheduleReset: reset,
  };
}

// WRITE — delete.
export function useDeleteSchedule() {
  const queryClient = useQueryClient();
  const { mutate, mutateAsync, isPending, error, reset } = useMutation<unknown, ApiError, string>({
    mutationFn: (id) => fetchWrapper.delete(`${BASE}/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["schedules"] }),
  });
  return {
    deleteSchedule: mutate,
    deleteScheduleAsync: mutateAsync,
    deleteSchedulePending: isPending,
    deleteScheduleError: error as Ref<ApiError | null>,
    deleteScheduleReset: reset,
  };
}

// WRITE — pause / resume.
export function usePauseSchedule() {
  const queryClient = useQueryClient();
  const { mutate, mutateAsync, isPending, error } = useMutation<unknown, ApiError, string>({
    mutationFn: (id) => fetchWrapper.post(`${BASE}/${id}/pause`),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ["schedules"] });
      queryClient.invalidateQueries({ queryKey: ["schedule", id] });
    },
  });
  return { pauseSchedule: mutate, pauseScheduleAsync: mutateAsync, pauseSchedulePending: isPending, pauseScheduleError: error as Ref<ApiError | null> };
}

export function useResumeSchedule() {
  const queryClient = useQueryClient();
  const { mutate, mutateAsync, isPending, error } = useMutation<unknown, ApiError, string>({
    mutationFn: (id) => fetchWrapper.post(`${BASE}/${id}/resume`),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ["schedules"] });
      queryClient.invalidateQueries({ queryKey: ["schedule", id] });
    },
  });
  return { resumeSchedule: mutate, resumeScheduleAsync: mutateAsync, resumeSchedulePending: isPending, resumeScheduleError: error as Ref<ApiError | null> };
}

// WRITE — run now (returns the started workflow run id).
export function useRunScheduleNow() {
  const queryClient = useQueryClient();
  const { mutate, mutateAsync, isPending, error } = useMutation<string, ApiError, string>({
    mutationFn: (id) => fetchWrapper.post<string>(`${BASE}/${id}/run`),
    onSuccess: (_runId, id) => {
      // Also refresh the list so the card's Last Run / status updates promptly after a manual run.
      queryClient.invalidateQueries({ queryKey: ["schedules"] });
      queryClient.invalidateQueries({ queryKey: ["schedule", id] });
      queryClient.invalidateQueries({ queryKey: ["schedule-runs", id] });
    },
  });
  return { runScheduleNow: mutate, runScheduleNowAsync: mutateAsync, runScheduleNowPending: isPending, runScheduleNowError: error as Ref<ApiError | null> };
}

// READ (on demand) — preview the next N occurrences for a cron + timezone (DST-correct, server-side).
export function usePreviewNextRuns() {
  return (payload: PreviewNextRunsRequest) =>
    fetchWrapper.post<string[]>(`${BASE}/preview-next`, payload);
}
